import { parseISO, format, isValid, parse } from 'date-fns';

/**
 * Safely parses a date string into a Date object.
 * Handles ISO strings, various common formats, and falls back to current date if invalid.
 * 
 * @param {string|Date} dateInput - The date to parse
 * @returns {Date} A valid Date object (or new Date() if parsing fails completely)
 */
export const safeParseDate = (dateInput) => {
    if (!dateInput) return new Date();
    if (dateInput instanceof Date) return dateInput;

    try {
        // 1. Try standard ISO parse first (fastest/safest)
        const isoDate = parseISO(dateInput);
        if (isValid(isoDate)) return isoDate;

        // 2. Try native Date parse (for things like "November 28, 2023")
        const nativeDate = new Date(dateInput);
        if (isValid(nativeDate)) return nativeDate;

        // 3. Try manual parsing for common formats if needed (e.g. MM/DD/YYYY)
        // This is a fallback for really messy data
        const formats = ['MM/dd/yyyy', 'yyyy-MM-dd', 'dd/MM/yyyy'];
        for (const fmt of formats) {
            const parsed = parse(dateInput, fmt, new Date());
            if (isValid(parsed)) return parsed;
        }

        console.warn(`Could not parse date: ${dateInput}, falling back to now`);
        return new Date();
    } catch (error) {
        console.error(`Error parsing date: ${dateInput}`, error);
        return new Date();
    }
};

/**
 * Formats a date safely.
 * 
 * @param {string|Date} dateInput - The date to format
 * @param {string} formatStr - The format string (default: 'PP') -> 'Nov 29, 2023'
 * @returns {string} Formatted date string
 */
export const formatDate = (dateInput, formatStr = 'PP') => {
    const date = safeParseDate(dateInput);
    try {
        return format(date, formatStr);
    } catch {
        // If formatting fails, return current date formatted
        return format(new Date(), formatStr);
    }
};
